import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  DatePicker,
  Input,
  Upload,
  TimePicker,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { STATUS_CODE } from "common/Constants";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import { useTranslation } from "react-i18next";

const ModalWrap = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
  }
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
  }
  .custom-upload .ant-upload {
    width: 100%;
  }

  .upload-box {
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: #cfd6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #cfd6ff;
  }

  .upload-box:hover {
    background: #bcc6ff;
  }
`;

const { TextArea } = Input;

const ApplyOvertime = ({ open, onCancel, data = {}, getData = () => {} }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const userInfo = useSelector(getUserInfo);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (data && Object.keys(data).length) {
      form.setFieldsValue({
        date: data?.overtimeDate ? dayjs(data.overtimeDate) : null,
        duration: data?.overtimeTime ? dayjs(data.overtimeTime) : null,
        reason: data?.overtimeReason || "",
        attachment: data?.fileName
          ? [
              {
                uid: "-1",
                name: data.fileName,
                status: "done",
                url: data.fileUrl || "",
              },
            ]
          : [],
      });
    }
  }, [data]);

  const handleFinish = async (values) => {
    try {
      setLoading(true);

      const fileList = values?.attachment || [];
      const fileObj = fileList?.[0]?.originFileObj || null;

      let uploadedKey = data?.objectKey || "";
      let fileName = data?.fileName || "";

      if (fileList.length === 0) {
        uploadedKey = "";
        fileName = "";
      }

      if (fileObj) {
        const videoTypes = [
          "video/mp4",
          "video/mpeg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-ms-wmv",
          "video/webm",
          "video/ogg",
        ];

        if (
          videoTypes.includes(fileObj.type) ||
          fileObj.type?.startsWith("video/")
        ) {
          message.error(t("Video files are not allowed"));
          return;
        }

        uploadedKey = await handleDocumentSubmit(fileObj);
        fileName = fileObj.name;
        if (!uploadedKey) return;
      }

      const payload = {
        overtimeDate: values.date.format("YYYY-MM-DD"),
        overtimeTime: values?.duration,
        overtimeReason: values.reason,
        reportingManager: userInfo?.reportingmanager,
        statusId: data?.requestId ? data?.statusId : "0",
        objectKey: uploadedKey || "",
        fileName: fileName,
      };

      if (data?.requestId) {
        payload.rejectReason = "";
      }

      const url = data?.requestId
        ? `employeerequest/${userInfo?.userid}/${data?.requestId}?requestType=overtimerequest`
        : `employeerequest/overtimerequest`;

      const method = data?.requestId ? "patch" : "post";

      const { code } = await CALL_API(url, method, payload);

      if (code === STATUS_CODE.SUCCESS) {
        await getData();
        setPopupOpen(true);

        setTimeout(() => {
          setPopupOpen(false);
          form.resetFields();
          onCancel();
        }, 2000);
      } else if (code === STATUS_CODE.RECORD_EXIST) {
        message.error(t("Overtime request already exists"));
      }
    } catch (err) {
      message.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentSubmit = async (file) => {
    try {
      const initPayload = {
        filename: file.name,
        type: file.type,
      };

      const res = await CALL_API(`employeerequest/file`, "POST", initPayload);

      if (res?.code !== STATUS_CODE.SUCCESS) {
        throw new Error("Failed to get upload url");
      }

      const uploadUrl = res?.url;
      const key = res?.key;

      if (!uploadUrl) throw new Error("Upload URL missing");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!putRes.ok) throw new Error("Upload failed");

      return key;
    } catch (err) {
      message.error(t("File upload failed"));
      return "";
    }
  };

  return (
    <ModalWrap
      title={
        data?.requestId ? t("Editing For Overtime") : t("Applying for Overtime")
      }
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      centered
      width={580}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label={t("Select the date")}
          name="date"
          rules={[{ required: true, message: t("Date is required") }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format={"DD-MM-YYYY"}
            placeholder={t("Choose")}
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        </Form.Item>

        <Form.Item
          label={t("Specify the amount of time")}
          name="duration"
          rules={[
            { required: true, message: t("Duration is required") },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();

                const totalSeconds =
                  value.hour() * 3600 + value.minute() * 60 + value.second();
                const maxSeconds = 12 * 3600;

                if (totalSeconds > maxSeconds) {
                  return Promise.reject(
                    new Error(t("Maximum duration is 12 hours"))
                  );
                }

                if (totalSeconds === 0) {
                  return Promise.reject(
                    new Error(t("Duration must be greater than 0"))
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <TimePicker
            format="HH:mm:ss"
            style={{ width: "100%" }}
            placeholder="HH:MM:SS"
            defaultOpenValue={dayjs("00:00:00", "HH:mm:ss")}
            showNow={false}
          />
        </Form.Item>

        <Form.Item
          label={t("Reason for Overtime")}
          name="reason"
          rules={[
            { required: true, message: t("Reason is required") },
            { max: 250, message: t("Max 250 chars") },
          ]}
        >
          <TextArea rows={4} maxLength={250} />
        </Form.Item>

        <Form.Item
          label={t("Attachment (Optional)")}
          name="attachment"
          valuePropName="fileList"
          getValueFromEvent={(e) => e?.fileList}
        >
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            className="custom-upload"
          >
            {!form.getFieldValue("attachment")?.length && (
              <div className="upload-box">
                <UploadOutlined />
                <span style={{ marginLeft: 8 }}>{t("Upload File")}</span>
              </div>
            )}
          </Upload>
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <ButtonOutlined
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            {t("Cancel")}
          </ButtonOutlined>

          <ButtonFilled type="primary" htmlType="submit" loading={loading}>
            {data?.requestId ? t("Save Changes") : t("Submit")}
          </ButtonFilled>
        </div>
      </Form>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type={"success"}
        message={data?.requestId ? t("Saved") : t("Submitted")}
      />
    </ModalWrap>
  );
};

export default ApplyOvertime;
